import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GlobalCompany, GridOverrides, TeamSettingResponse, TeamSettingSection } from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { FormDataService, TabComponent } from '@mds/ui';
import { ComposerApiService } from '@services/composer-api.service';
import { GenericApiService } from '@services/generic-api.service';
import { DATA_SOURCES_SECTION_NAME } from '../components/data-sources-settings/constants/data-source.constants';
import { DataSourcesSettingsComponent } from '../components/data-sources-settings/data-sources-settings.component';
import { EpdSectionChange, EpdSettingsComponent } from '../components/epd-settings/epd-settings.component';
import { MemberSettingsComponent } from '../components/member-settings/member-settings.component';
import { ScipSettingsComponent } from '../components/scip-settings/scip-settings.component';
import { SsoProviderSettingsComponent } from '../components/sso-provider-settings/sso-provider-settings.component';
import { SsoSettingsComponent } from '../components/sso-settings/sso-settings.component';
import { TeamSettingsTabComponent } from '../components/team-settings-tab/team-settings-tab.component';

@Component({
  selector: 'app-team-settings',
  standalone: true,
  imports: [
    ScipSettingsComponent,
    SsoSettingsComponent,
    EpdSettingsComponent,
    TeamSettingsTabComponent,
    MemberSettingsComponent,
    SsoProviderSettingsComponent,
    DataSourcesSettingsComponent,
    MdsIconsModule,
    TabComponent
  ],
  templateUrl: './team-settings.component.html',
  styleUrls: ['./team-settings.component.scss'],
  providers: [
    {
      provide: FormDataService,
      useFactory: (genericApi: GenericApiService, composerApi: ComposerApiService) =>
        new FormDataService({
          getBaseData: genericApi.getBaseData.bind(genericApi),
          getTileCards: composerApi.getTileCards.bind(composerApi)
        }),
      deps: [GenericApiService, ComposerApiService]
    }
  ]
})
export class TeamSettingsComponent implements OnInit {
  icons = MdsIcon;
  tabs = [
    { label: 'Members' },
    { label: 'Team Settings' },
    { label: 'EPD' },
    { label: 'SSO' },
    { label: 'SSO Providers' },
    { label: 'Data Sources' }
  ];
  selectedTab = 'Members';

  userRid: string;
  teamRid: string;
  teamName: string;
  teamMembers: any[];
  isUserTeamAdmin: boolean;
  teamInfo: any;
  actions: any;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  showInvite: boolean;
  myControl = new UntypedFormControl('');
  overrides: GridOverrides;
  options: { minHeight: string };
  scipTabData = {
    name: 'scip_legal_entity',
    app_name: 'SCIP Legal Entity',
    tool: 'composer/new/scip_legal_entity'
  };

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;
  @ViewChild('companyAutocomplete') companyAutocomplete: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  suggestions: GlobalCompany[] = [];
  page = 1;
  isLoading = false;
  scipSection!: TeamSettingSection;
  teamSettingSection!: TeamSettingSection;
  epdSection!: TeamSettingSection;
  epdThemeSection!: TeamSettingSection;
  dataSourcesSection!: TeamSettingSection;

  constructor(
    private composerApiService: ComposerApiService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<TeamSettingsComponent>
  ) {}

  ngOnInit(): void {
    if (localStorage.getItem('user_info')) {
      this.init();
    }
  }

  init() {
    this.userRid = JSON.parse(localStorage.getItem('user_info')).rid;
    this.teamRid = JSON.parse(localStorage.getItem('team_info')).team_rid;
    this.teamName = JSON.parse(localStorage.getItem('team_info')).team_name;
    this.userRid = this.userRid.replace('#', '');

    if (this.teamRid) {
      this.loadTeamData();
    }
  }

  loadTeamData() {
    this.composerApiService.getComposerData(this.teamRid).subscribe((res: TeamSettingResponse[]) => {
      res.forEach((row) => {
        if (row.sections) {
          this.getTeamInfo(row.sections);
        }
        if (row.actions) {
          this.actions = row.actions;
        }
      });
    });
  }

  getTeamInfo(data: TeamSettingSection[]) {
    for (const section of data) {
      switch (section.name) {
        case 'Members':
          this.teamInfo = section;
          for (const line of section.lines) {
            const info = this.getMemberInfo(line);
            info.lines = line;
            if (this.userRid.replace('#', '') === info.rid?.replace('#', '')) {
              this.isUserTeamAdmin = info.is_admin;
            }
          }
          break;
        case 'Team Settings':
          this.teamSettingSection = section;
          break;
        case 'Team EPD Settings':
          this.epdSection = section;
          break;
        case this.scipTabData.app_name:
          this.scipSection = section;
          this.addTabAfter('SCIP Legal Entity', 'SSO Providers');
          break;
        case 'EPD Theme Settings':
          this.epdThemeSection = section;
          this.addTabAfter('Color Palette', 'EPD');
          break;
        case DATA_SOURCES_SECTION_NAME:
          this.dataSourcesSection = section;
          break;
        default:
          break;
      }
    }
  }

  private addTabAfter(label: string, afterLabel: string): void {
    if (this.tabs.some((tab) => tab.label === label)) {
      return;
    }
    const anchorIndex = this.tabs.findIndex((tab) => tab.label === afterLabel);
    this.tabs.splice(anchorIndex + 1, 0, { label });
  }

  getMemberInfo(data) {
    const membersData: Record<string, any> = {};
    data.forEach((e) => {
      if (e.key.endsWith('.in')) {
        Object.assign(membersData, e);
      } else if (e.key.endsWith('.rid')) {
        membersData.isUserTeamManager = e.label === 'Manager';
      } else {
        const key = e.key.split('.').pop()!;
        membersData[key] = e.value;
      }
    });
    return membersData;
  }

  closeDialog() {
    this.dialogRef.close();
  }

  setActiveTab(selectedTab: any): void {
    this.selectedTab = selectedTab.label;
  }

  updateEpdSection(event: EpdSectionChange): void {
    if (event.tab === 'EPD') {
      this.epdSection = event.section;
      return;
    }

    this.epdThemeSection = event.section;
  }
}
